import { Mutex } from "async-mutex";
import { create } from "zustand";
import {
  createJSONStorage,
  persist,
  subscribeWithSelector,
} from "zustand/middleware";
import * as SharedChatCommandSchema from "../../darvin-shared/schema/zod/agent/chat-command.schema";
import * as SharedChatResponse from "../../darvin-shared/schema/zod/agent/chat-response.schema";
import * as SharedMessageTypes from "../../darvin-shared/schema/zod/agent/message-types.schema";
import { ChatApi } from "../api/chatApi";
import { HttpNotEnoughTokensException } from "../model/httpException";
import {
  SseConnectionManager,
  SseMessageHandler,
} from "../services/sseConnectionManager";
import { useModelStore } from "./useModelStore";

export enum ProjectError {
  NOT_ENOUGH_TOKENS = "notEnoughTokens",
  TIMEOUT = "timeout",
}

const mutex = new Mutex();

interface ProjectActions {
  initialize: (
    projectId: string,
    initialPrompt: string | null,
    initialImage: File | null
  ) => Promise<void>;
  sendMessage: (message: string, image: File | null) => Promise<void>;
  fixCompilationErrorForMessage: (messageId: string) => Promise<void>;
  rollback: (
    checkpointId?: string,
    originalCheckpointId?: string
  ) => Promise<void>;
  dispose: () => void;
  disposeSSE: () => void;
  refresh: () => Promise<void>;
}

interface ProjectState {
  isNewProject: boolean | null;
  projectId: string | null;
  history: SharedChatResponse.MessageBody[] | null;
  latestMsg: SharedChatResponse.MessageBody | null;
  error: ProjectError | null;

  actions: ProjectActions;
}

export const useProjectStore = create<ProjectState>()(
  persist(
    subscribeWithSelector((set, get) => {
      let connectionManager: SseConnectionManager | undefined = undefined;
      let inactivityTimer: NodeJS.Timeout | undefined = undefined;
      let oneTimeRefreshOnVisibleHandler: ((event: Event) => void) | undefined =
        undefined;
      let visibilityHandlerForInactivityCheck:
        | ((event: Event) => void)
        | undefined = undefined;
      let inactivityStartTime: number | undefined = undefined;
      const INACTIVITY_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
      let shouldReloadHistoryOnChatMessage: boolean = false;
      let idOfMessageToReloadChatHistoryAfter: string | null = null;

      function registerVisibilityHandlerForInactivityCheck() {
        if (!visibilityHandlerForInactivityCheck) {
          visibilityHandlerForInactivityCheck = async () => {
            if (document.visibilityState === "visible") {
              if (isInactivityTimeout()) {
                await onInactivityTimeout();
              }
            }
          };
          document.addEventListener(
            "visibilitychange",
            visibilityHandlerForInactivityCheck
          );
        }
      }

      function unregisterVisibilityHandlerForInactivityCheck() {
        if (visibilityHandlerForInactivityCheck) {
          document.removeEventListener(
            "visibilitychange",
            visibilityHandlerForInactivityCheck
          );
          visibilityHandlerForInactivityCheck = undefined;
        }
      }

      function registerOneTimeRefreshOnVisibleHandler() {
        if (oneTimeRefreshOnVisibleHandler) {
          document.removeEventListener(
            "visibilitychange",
            oneTimeRefreshOnVisibleHandler
          );
        }
        oneTimeRefreshOnVisibleHandler = async () => {
          if (document.visibilityState === "visible") {
            await get().actions.refresh();
            document.removeEventListener(
              "visibilitychange",
              oneTimeRefreshOnVisibleHandler!
            );
            oneTimeRefreshOnVisibleHandler = undefined;
          }
        };
        document.addEventListener(
          "visibilitychange",
          oneTimeRefreshOnVisibleHandler
        );
      }

      function unregisterOneTimeRefreshOnVisibleHandler() {
        if (oneTimeRefreshOnVisibleHandler) {
          document.removeEventListener(
            "visibilitychange",
            oneTimeRefreshOnVisibleHandler
          );
          oneTimeRefreshOnVisibleHandler = undefined;
        }
      }

      function clearError(errorToClear: ProjectError) {
        if (get().error !== errorToClear) {
          return;
        }
        set({ error: null });
      }

      function setError(errorToSet: ProjectError) {
        set({ error: errorToSet });
      }

      function stopInactivityTimer() {
        if (inactivityTimer) {
          clearTimeout(inactivityTimer);
        }
        inactivityTimer = undefined;
        inactivityStartTime = undefined;
      }

      function startInactivityTimer(startTime?: number) {
        stopInactivityTimer();

        // Record when the timer started
        const now = Date.now();
        inactivityStartTime = startTime ? startTime : now;
        const elapsedSinceStart = now - inactivityStartTime;
        if (elapsedSinceStart >= INACTIVITY_TIMEOUT_MS) {
          // Timeout is already due in moment of staring the timer
          onInactivityTimeout();
          return;
        }

        // Set up the timeout as a fallback for when tab is active
        inactivityTimer = setTimeout(
          onInactivityTimeout,
          INACTIVITY_TIMEOUT_MS - elapsedSinceStart
        );
      }

      async function onInactivityTimeout() {
        stopInactivityTimer();
        setError(ProjectError.TIMEOUT);
        await loadChatHistory();
      }

      function isInactivityTimeout() {
        if (!inactivityStartTime) {
          return false;
        }
        const elapsedTime = Date.now() - inactivityStartTime;
        return elapsedTime >= INACTIVITY_TIMEOUT_MS;
      }

      function isTerminalMessage(msg: SharedChatResponse.MessageBody) {
        return (
          msg.type === SharedMessageTypes.MessageTypeEnum.enum.jobDone ||
          msg.type === SharedMessageTypes.MessageTypeEnum.enum.error
        );
      }

      function isMessageTriggeringHistoryReload(
        msg: SharedChatResponse.MessageBody
      ) {
        // Job done for rollbacks should reload history no matter what
        return (
          msg.type === SharedMessageTypes.MessageTypeEnum.enum.jobDone &&
          msg.content.includes("command-rollback")
        );
      }

      function disposeConnectionManager() {
        connectionManager?.dispose();
        connectionManager = undefined;
      }

      function createConnectionManager(messageHandler: SseMessageHandler) {
        const projectId = get().projectId;
        if (!projectId) {
          console.warn(
            "Failed to create connection manager: Project ID is not set"
          );
          return undefined;
        }
        disposeConnectionManager();
        connectionManager = new SseConnectionManager(projectId, messageHandler);
        return connectionManager;
      }

      async function startListening() {
        await createConnectionManager({
          onMessage: async (msg) => {
            try {
              const data = JSON.parse(msg.data);
              const body = SharedChatResponse.MessageBodySchema.parse(
                data.body
              );
              console.log("Event received in SSE", data.body);
              await handleSseMessage(body);
            } catch (error) {
              console.error("Error parsing SSE message:", error);
            }
          },
          onAbort: (wasManualAbort: boolean) => {
            if (wasManualAbort) {
              return;
            }
            registerOneTimeRefreshOnVisibleHandler();
          },
        })?.start();
      }

      async function loadChatHistory(params?: {
        messageIdToOmit?: string;
        isFirstLoad?: boolean;
      }) {
        const { messageIdToOmit, isFirstLoad = false } = params || {};
        const history = await ChatApi.loadChatHistory(get().projectId!);
        processHistory({ history, messageIdToOmit, isFirstLoad });
      }

      function processHistory({
        history,
        messageIdToOmit,
        isFirstLoad = false,
      }: {
        history: SharedChatResponse.MessageBody[];
        messageIdToOmit?: string;
        isFirstLoad: boolean;
      }) {
        console.log("Loaded history", history);

        if (messageIdToOmit) {
          history = history.filter((msg) => msg.messageId !== messageIdToOmit);
        }
        const latestMsg = history.at(-1) ?? null;
        checkLastHistoryMessageTimeout(latestMsg);

        // First load in the middle of the conversation stream can result in
        // incomplete chat state, so reload full history on next message
        shouldReloadHistoryOnChatMessage =
          isFirstLoad && (latestMsg == null || !isTerminalMessage(latestMsg));

        set({ history, latestMsg });
      }

      function checkLastHistoryMessageTimeout(
        lastMessage: SharedChatResponse.MessageBody | null
      ) {
        if (!lastMessage) {
          return;
        }
        if (isTerminalMessage(lastMessage)) {
          // Last message is terminal one, this is a desired state,
          // no error, no need to check timeout
          stopInactivityTimer();
          clearError(ProjectError.TIMEOUT);
          return;
        }

        const elapsedTimeSinceLastMsg = Date.now() - lastMessage.timestamp;
        if (elapsedTimeSinceLastMsg > INACTIVITY_TIMEOUT_MS) {
          // Last message is outdated - set error timeout and
          // stop checking inactivity timeout (timeout happened here)
          stopInactivityTimer();
          setError(ProjectError.TIMEOUT);
        } else {
          // Valid non terminal message - no error, but check for
          // inactivity since that last message
          startInactivityTimer(lastMessage.timestamp);
          clearError(ProjectError.TIMEOUT);
        }
      }

      function onProjectActivity({
        finalActivity,
      }: {
        finalActivity: boolean;
      }) {
        clearError(ProjectError.TIMEOUT);
        if (finalActivity) {
          stopInactivityTimer();
          unregisterVisibilityHandlerForInactivityCheck();
        } else {
          startInactivityTimer();
          registerVisibilityHandlerForInactivityCheck();
        }
      }

      async function handleSseMessage(msg: SharedChatResponse.MessageBody) {
        await mutex.runExclusive(async () => {
          onProjectActivity({ finalActivity: isTerminalMessage(msg) });

          if (isMessageTriggeringHistoryReload(msg)) {
            await loadChatHistory();
          }

          if (
            msg.type ===
              SharedMessageTypes.MessageTypeEnum.enum.projectConfig ||
            msg.type === SharedMessageTypes.MessageTypeEnum.enum.general ||
            msg.type === SharedMessageTypes.MessageTypeEnum.enum.tools ||
            msg.type === SharedMessageTypes.MessageTypeEnum.enum.error
          ) {
            if (shouldReloadHistoryOnChatMessage) {
              if (
                (idOfMessageToReloadChatHistoryAfter &&
                  msg.messageId !== idOfMessageToReloadChatHistoryAfter) ||
                !msg.stream ||
                ((msg.chunk?.delta?.length ?? 0) > 0 &&
                  (msg.chunk!.delta[0].path === "/topic" ||
                    msg.chunk!.delta[0].path === "/files"))
              ) {
                await loadChatHistory({ messageIdToOmit: msg.messageId });
                shouldReloadHistoryOnChatMessage = false;
                idOfMessageToReloadChatHistoryAfter = null;
              } else if (!idOfMessageToReloadChatHistoryAfter) {
                idOfMessageToReloadChatHistoryAfter = msg.messageId;
              }
            }
          }
          set({ latestMsg: msg });
        });
      }

      return {
        isNewProject: null,
        projectId: null,
        history: null,
        latestMsg: null,
        error: null,

        actions: {
          initialize: async (
            projectId: string,
            initialPrompt: string | null,
            initialImage: File | null
          ) => {
            if (get().projectId) {
              await loadChatHistory({ isFirstLoad: true });
              startListening();
              set({ isNewProject: false });
              return;
            }

            set({ isNewProject: initialPrompt ? true : false, projectId });

            await loadChatHistory({ isFirstLoad: true });
            startListening();

            if (initialPrompt) {
              try {
                await get().actions.sendMessage(initialPrompt, initialImage);
              } catch (error) {
                // HttpNotEnoughTokensException handled by setError() subscription
                // which triggers the error message display
                if (!(error instanceof HttpNotEnoughTokensException)) {
                  throw error;
                }
              }
            }
          },

          sendMessage: async (message: string, image: File | null) => {
            try {
              onProjectActivity({ finalActivity: false });
              const model = useModelStore.getState().selectedModel;
              await ChatApi.sendMessage(get().projectId!, message, image, model);

              // If call succeeded it means we have enough tokens
              clearError(ProjectError.NOT_ENOUGH_TOKENS);
            } catch (error) {
              if (error instanceof HttpNotEnoughTokensException) {
                setError(ProjectError.NOT_ENOUGH_TOKENS);
                throw error; // Re-throw so caller knows message was not sent
              } else {
                throw error;
              }
            }
          },

          fixCompilationErrorForMessage: async (messageId: string) => {
            await ChatApi.sendCommand(get().projectId!, {
              command:
                SharedChatCommandSchema.ChatCommandTypeEnum.enum
                  .fixCompilationError,
              args: {
                errorMessageId: messageId,
              },
            });
          },

          rollback: async (
            checkpointId?: string,
            originalCheckpointId?: string
          ) => {
            let finalCheckpointId = checkpointId;

            if (!finalCheckpointId) {
              const history = await ChatApi.loadChatHistory(get().projectId!);
              if (history === null || history.length === 0) {
                return;
              }

              const latestUserMessage = [...history]
                .reverse()
                .find(
                  (msg) =>
                    msg.type ===
                    SharedMessageTypes.MessageTypeEnum.enum.userMessage
                );

              finalCheckpointId = latestUserMessage?.checkpointId;
            }

            if (!finalCheckpointId) {
              throw new Error("No checkpoint for rollback found!");
            }

            try {
              const args: {
                checkpointId: string;
                originalCheckpointId?: string;
              } = {
                checkpointId: finalCheckpointId!,
              };

              if (originalCheckpointId) {
                args.originalCheckpointId = originalCheckpointId;
              }

              await ChatApi.sendCommand(get().projectId!, {
                command:
                  SharedChatCommandSchema.ChatCommandTypeEnum.enum.rollback,
                args,
              });
              clearError(ProjectError.NOT_ENOUGH_TOKENS);
            } catch (error) {
              if (error instanceof HttpNotEnoughTokensException) {
                setError(ProjectError.NOT_ENOUGH_TOKENS);
                throw error; // Re-throw so caller knows rollback was not executed
              } else {
                throw error;
              }
            }
          },

          dispose: () => {
            disposeConnectionManager();
            stopInactivityTimer();
            unregisterVisibilityHandlerForInactivityCheck();
            unregisterOneTimeRefreshOnVisibleHandler();

            set({
              projectId: null,
              history: null,
              latestMsg: null,
            });
          },

          disposeSSE: () => {
            if (connectionManager) {
              connectionManager.dispose();
              connectionManager = undefined;
            }
          },

          refresh: async () => {
            await loadChatHistory({ isFirstLoad: true });
          },
        },
      };
    }),
    {
      name: "project-storage",
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        projectId: state.projectId,
      }),
      version: 1,
    }
  )
);

export const useIsNewProject = () =>
  useProjectStore((state) => state.isNewProject);

export const useProjectId = () => useProjectStore((state) => state.projectId);

export const useProjectMessages = () =>
  useProjectStore((state) => state.history);

export const useProjectActions = () =>
  useProjectStore((state) => state.actions);
